import { cn } from "@edusogno/lib/utils";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IFinalStepData } from "@/pages/Registration";
import { FindUserResponse, IObLangauges } from "@/types";
import routes from "@/config/routes.ts";
import AuthContent from "@edusogno/layouts/auth-content.js";
import WarningIcon from "@/components/icons/book-exam/WarningIcon.tsx";
import LoadingButton from "@edusogno/primitives/loading-button";
import AuthPageFooter from "@edusogno/layouts/auth-page-footer.js";
import RegistrationHeader from "@/components/auth/registration/Header.tsx";
import RegistrationContent from "@/components/auth/registration/Content.tsx";
import RegistrationContainer from "@/components/auth/registration/Container.tsx";

interface RegistrationFinishProps {
  data: IFinalStepData;
  user: FindUserResponse;
  hasOneToOne: boolean;
}

const RegistrationFinish = ({
  data,
  user,
  hasOneToOne,
}: RegistrationFinishProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const lang = data?.selectedLanguage || user?.lang;

  const monthToCompare = data?.selectedStartMonth || user?.start_month;

  const firstMonth = user?.init_dates?.[0]?.[lang]?.value;

  const showOnboardingProcess =
    (lang === "it" && monthToCompare === firstMonth) ||
    (lang === "it" && hasOneToOne);

  const redirectLogin = routes.login;

  // @typescript-eslint/naming-convention
  const linkOnboarding: string | IObLangauges =
    user.payments?.[user?.payments.length - 1]?.product?.course_info
      ?.link_onboarding;

  const redirect = !showOnboardingProcess
    ? "/user/"
    : (typeof linkOnboarding == "string"
        ? linkOnboarding
        : linkOnboarding?.[lang]) ?? redirectLogin;

  const handleRedirect = () => {
    if (showOnboardingProcess) {
      window.open(redirect, "_blank");
    }

    navigate(redirectLogin, { replace: true });
  };

  return (
    <>
      <AuthContent className="flex flex-col items-center h-full px-0">
        <RegistrationContainer>
          <RegistrationHeader
            logo="finish"
            title={t("auth.registration.finish.title")}
            subtitle={t("auth.registration.finish.subtitle")}
            className={cn("h-fit", {
              ["[&_p]:hidden"]: !showOnboardingProcess,
            })}
          />

          <RegistrationContent>
            <div className="flex flex-row items-start pt-4 gap-4 justify-center h-full w-full">
              <WarningIcon className="fill-brand-secondary w-6 h-auto" />

              <p className="text-sm text-brand">
                {t("auth.registration.finish.mandatory")}
              </p>
            </div>
          </RegistrationContent>
        </RegistrationContainer>
      </AuthContent>

      <AuthPageFooter className="mt-auto">
        <div className="font-normal text-lg flex flex-row items-center justify-center md:text-lg  pt-4 mb-3 text-center text-brand w-full px-1" />

        <div className="w-full max-w-xl mx-auto flex gap-x-5 items-center px-6 mb-4">
          <LoadingButton
            size="lg"
            type="button"
            color={showOnboardingProcess ? "green" : "white"}
            onClick={() => handleRedirect()}
            className="w-full tracking-wide"
          >
            {showOnboardingProcess
              ? t("auth.registration.finish.go_to_onboarding")
              : t("auth.registration.finish.go_to_login")}
          </LoadingButton>
        </div>
      </AuthPageFooter>
    </>
  );
};

export default RegistrationFinish;
